import React, { useEffect } from 'react';
import { createGlobalState } from 'services/createGlobalState';
import { Vehicle } from 'core/models/vehicle';
import { SubScreenLayout } from 'core/models/layout';
import { useWithProjects } from 'services/useProjects';
import { useWithSelectedProject, useSelectProject } from 'services/useSelectedProject';
import { useMultiScreenReceiver } from 'services/multiscreen/hooks/useMultiScreenReceiver';
import { useMultiScreenClient } from 'services/multiscreen/hooks/useMultiScreenClient';
import { useOIDCClient } from 'services/oidc';
import { useWithEnvironments } from 'services/useEnvironments';
import { useWithScope } from 'services/useScope';
import { withAdaptors } from 'services/utility/withAdaptors';
import { useWithSelectedEnvironment, useSelectEnvironment } from 'services/useSelectedEnvironment';

const {
    useWithState: useWithVehicleId,
    useState: useRemoteVehicleId,
    useDispatch: useSetVehicleId,
} = createGlobalState<Vehicle.Id | undefined>((_, v) => v, undefined);

const {
    useWithState: useWithSubScreenLayouts,
    useState: useSubScreenLayouts,
    useDispatch: useSetSubScreenLayouts,
} = createGlobalState<SubScreenLayout[] | undefined>((_, v) => v, undefined);

const {
    useWithState: useWithNumberOfScreens,
    useState: useNumberOfScreens,
    useDispatch: useSetNumberOfScreens,
} = createGlobalState<number>((_, v) => v, 1);

function Outer(props: React.PropsWithChildren<{}>) {
    const { children } = props;

    const withProjects = useWithProjects();
    const withSelectedProject = useWithSelectedProject();
    const withEnvironments = useWithEnvironments();
    const withScopes = useWithScope();
    const withSelectedEnvironment = useWithSelectedEnvironment();
    const withVehicleId = useWithVehicleId();
    const withCameraLayout = useWithSubScreenLayouts();
    const withNumberOfScreens = useWithNumberOfScreens();

    return withAdaptors(
        withProjects,
        withSelectedProject,
        withEnvironments,
        withSelectedEnvironment,
        withScopes,
        withVehicleId,
        withCameraLayout,
        withNumberOfScreens,
    )(<React.Fragment>{children}</React.Fragment>);
}

function Inner(props: React.PropsWithChildren<{}>) {
    const { children } = props;

    const receiver = useMultiScreenReceiver();
    const client = useMultiScreenClient(receiver);
    const oidcClient = useOIDCClient();
    const selectProject = useSelectProject();
    const selectEnvironment = useSelectEnvironment();
    const setVehicleId = useSetVehicleId();
    const setCameraLayouts = useSetSubScreenLayouts();
    const setNumberOfScreens = useSetNumberOfScreens();
    useEffect(() => {
        console.debug({ client, oidcClient });
        if (!client) return;
        if (!oidcClient) return;

        client.onTokenSet = tokenSet => oidcClient.setTokenSet(tokenSet);
        client.onSelectedProject = selectedProject => selectProject(selectedProject);
        client.onSelectedEnvironment = selectedEnvironment => selectEnvironment(selectedEnvironment);
        client.onVehicleId = vehicleId => setVehicleId(vehicleId ?? undefined);
        client.onCameraLayouts = layouts => setCameraLayouts(layouts ?? undefined);
        client.onNumberOfScreens = numberOfScreens => setNumberOfScreens(numberOfScreens);
        client.start();
    }, [client, oidcClient, selectEnvironment, selectProject, setCameraLayouts, setNumberOfScreens, setVehicleId]);

    return <React.Fragment>{children}</React.Fragment>;
}

export function withMultiScreenClientBinding(node: JSX.Element) {
    return (
        <Outer>
            <Inner>
                {node}
            </Inner>
        </Outer>
    );
}

export { useRemoteVehicleId, useSubScreenLayouts, useNumberOfScreens };
